import React, { useState, useEffect, useCallback, memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import {
  Users, UserPlus, Phone, Mail, MapPin, Calendar, Clock, DollarSign,
  MessageSquare, CheckCircle, XCircle, AlertTriangle, ChevronRight,
  Search, Filter, MoreVertical, Plus, Edit, Trash2, Eye, Send,
  Building, Tag, FileText, Sparkles, ArrowRight, RefreshCw, Snowflake, AlertOctagon,
  GripVertical, Trophy, Archive, Zap, Bot, Lock, Flame, ChevronDown, CalendarPlus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable
} from "@dnd-kit/core";
import confetti from "canvas-confetti";
import { usePermissions } from "../contexts/PermissionsContext";

// ================== VISUAL DECAY HELPER ==================
// Calculate hours since last activity for "Rotting Lead" feature
const getHoursSinceActivity = (lastActivityAt) => {
  if (!lastActivityAt) return 999; // Treat missing as very stale
  const lastActivity = new Date(lastActivityAt);
  const now = new Date();
  return (now - lastActivity) / (1000 * 60 * 60); // Convert ms to hours
};

// Get visual decay status based on hours since activity
const getDecayStatus = (lead) => {
  const hours = getHoursSinceActivity(lead.last_activity_at || lead.created_at);
  if (hours < 24) return "fresh";      // < 24 hours: Fresh
  if (hours < 48) return "warning";    // 24-48 hours: Warning (yellow)
  return "stagnant";                    // > 48 hours: Stagnant (red)
};

// Get decay styling classes for lead cards
const getDecayClasses = (decayStatus) => {
  switch (decayStatus) {
    case "warning":
      return "border-yellow-500 border-2";
    case "stagnant":
      return "bg-red-900/30 border-red-500 border-2";
    default:
      return "border-slate-700";
  }
};

// Lost Reasons for "The Why Button"
const LOST_REASONS = [
  { value: "price_too_high", label: "Price Too High" },
  { value: "competitor", label: "Chose Competitor" },
  { value: "ghosted", label: "Ghosted / No Response" },
  { value: "bad_fit", label: "Bad Fit" },
  { value: "timing", label: "Bad Timing" },
  { value: "other", label: "Other" }
];

// Constants
// NEW SIMPLIFIED 4-STAGE PIPELINE
const LEAD_STATUSES = [
  { value: "new_lead", label: "New Lead", color: "bg-blue-500" },
  { value: "site_visit", label: "Site Visit", color: "bg-purple-500" },
  { value: "estimate_sent", label: "Estimate Sent", color: "bg-orange-500" },
  { value: "follow_up", label: "Follow Up", color: "bg-pink-500" },
  { value: "won", label: "Won", color: "bg-emerald-500" },
  { value: "lost", label: "Lost", color: "bg-red-500" },
  { value: "on_hold", label: "On Hold", color: "bg-slate-500" }
];

// Pipeline stages for display (only the 4 main stages)
const PIPELINE_STAGES = [
  { value: "new_lead", label: "New Lead", color: "bg-blue-500" },
  { value: "site_visit", label: "Site Visit", color: "bg-purple-500" },
  { value: "estimate_sent", label: "Estimate Sent", color: "bg-orange-500" },
  { value: "follow_up", label: "Follow Up", color: "bg-pink-500" }
];

// All possible statuses for dropdown (includes archive states)
const ALL_LEAD_STATUSES = [
  { value: "new_lead", label: "New Lead", color: "bg-blue-500", textColor: "text-blue-400" },
  { value: "site_visit", label: "Site Visit", color: "bg-purple-500", textColor: "text-purple-400" },
  { value: "estimate_sent", label: "Estimate Sent", color: "bg-orange-500", textColor: "text-orange-400" },
  { value: "follow_up", label: "Follow Up", color: "bg-pink-500", textColor: "text-pink-400" },
  { value: "won", label: "Won / Sold", color: "bg-emerald-500", textColor: "text-emerald-400" },
  { value: "lost", label: "Lost / Archive", color: "bg-red-500", textColor: "text-red-400" },
  { value: "icebox", label: "Icebox / Nurture", color: "bg-cyan-500", textColor: "text-cyan-400" }
];

// Map legacy statuses to new simplified stages
const STATUS_MIGRATION_MAP = {
  "new": "new_lead",
  "contacted": "new_lead",
  "scheduled": "site_visit",
  "site_visit_completed": "site_visit",
  "estimate_in_progress": "estimate_sent",
  "estimate_sent": "estimate_sent",
  "follow_up_needed": "follow_up",
  // New statuses map to themselves
  "new_lead": "new_lead",
  "site_visit": "site_visit",
  "follow_up": "follow_up"
};

// Helper to get normalized status for pipeline display
const getNormalizedStatus = (status) => {
  return STATUS_MIGRATION_MAP[status] || status;
};

// Lead sources loaded at runtime from backend
const LEAD_SOURCES = [];

const SERVICE_TYPES = [
  { value: "remodel", label: "Remodel" },
  { value: "new_build", label: "New Build" },
  { value: "repair", label: "Repair" },
  { value: "hvac", label: "HVAC" },
  { value: "electrical", label: "Electrical" },
  { value: "plumbing", label: "Plumbing" },
  { value: "roofing", label: "Roofing" },
  { value: "painting", label: "Painting" },
  { value: "flooring", label: "Flooring" },
  { value: "landscaping", label: "Landscaping" },
  { value: "general_contracting", label: "General Contracting" },
  { value: "addition", label: "Addition" },
  { value: "bathroom", label: "Bathroom" },
  { value: "kitchen", label: "Kitchen" },
  { value: "other", label: "Other" }
];

const COMMUNICATION_TYPES = [
  { value: "call", label: "Phone Call" },
  { value: "text", label: "Text Message" },
  { value: "email", label: "Email" },
  { value: "voicemail", label: "Voicemail" },
  { value: "in_person", label: "In Person" },
  { value: "other", label: "Other" }
];

const NEXT_ACTIONS = [
  { value: "call", label: "Call" },
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "schedule_visit", label: "Schedule Visit" },
  { value: "send_estimate", label: "Send Estimate" },
  { value: "close_out", label: "Close Out" },
  { value: "none", label: "None" }
];

// Demo mode flag for seed / simulate controls
const DEMO_ENABLED = process.env.NODE_ENV !== "production" || process.env.REACT_APP_DEMO_MODE === "true";

// Status Badge Component - handles both legacy and new statuses
const StatusBadge = ({ status }) => {
  const normalizedStatus = getNormalizedStatus(status);
  const statusConfig = LEAD_STATUSES.find(s => s.value === normalizedStatus) || LEAD_STATUSES[0];
  return (
    <Badge className={`${statusConfig.color} text-white`}>
      {statusConfig.label}
    </Badge>
  );
};

// Priority Badge
const PriorityBadge = ({ priority }) => {
  const colors = {
    urgent: "bg-red-500 text-white",
    high: "bg-orange-500 text-white",
    medium: "bg-yellow-500 text-white",
    low: "bg-slate-400 text-white"
  };
  return (
    <Badge className={colors[priority] || colors.medium}>
      {priority?.charAt(0).toUpperCase() + priority?.slice(1)}
    </Badge>
  );
};

// Draggable Lead Card with Quick Actions
const DraggableLeadCard = memo(({ lead, onClick, onQuickCall, onQuickEmail }) => {
  const decayStatus = getDecayStatus(lead);
  const decayClasses = getDecayClasses(decayStatus);
  const hoursSinceActivity = Math.round(getHoursSinceActivity(lead.last_activity_at || lead.created_at));
  const hasWakeUpTag = lead.tags?.includes("WAKE UP");
  const hasAutoIntake = lead.tags?.includes("AUTO-INTAKE");
  const hasAutoSms = lead.auto_sms_sent_at || lead.communications?.some(c => c.is_auto);
  
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: lead.lead_id,
    data: { lead }
  });
  
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: isDragging ? 1000 : 1,
    opacity: isDragging ? 0.9 : 1,
    boxShadow: isDragging ? '0 6px 18px rgba(0,0,0,0.35)' : undefined,
    willChange: 'transform'
  } : undefined;

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`p-3 rounded-lg shadow-sm cursor-grab active:cursor-grabbing transition-all mb-2 ${
        decayStatus === "stagnant" ? "bg-red-900/30" : "bg-slate-800"
      } ${decayClasses} ${isDragging ? 'ring-2 ring-emerald-500 dragging' : ''}`}
      {...attributes}
      {...listeners}
    >
      {/* Drag Handle + Badges Row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1">
          <GripVertical className="h-4 w-4 text-slate-500" />
          {decayStatus === "stagnant" && (
            <Badge className="bg-red-600 text-white text-xs px-1.5 py-0">
              <AlertOctagon className="h-3 w-3 mr-0.5" />
              STAGNANT
            </Badge>
          )}
          {hasWakeUpTag && (
            <Badge className="bg-cyan-500 text-white text-xs px-1.5 py-0">
              <Sparkles className="h-3 w-3 mr-0.5" />
              WAKE UP
            </Badge>
          )}
          {hasAutoIntake && (
            <Badge className="bg-purple-500 text-white text-xs px-1.5 py-0">
              <Bot className="h-3 w-3 mr-0.5" />
              AUTO
            </Badge>
          )}
        </div>
        {/* Priority Badge - Fix NaN bug */}
        {lead.priority && lead.priority !== "undefined" && (
          <PriorityBadge priority={lead.priority} />
        )}
      </div>
      
      {/* Auto-SMS indicator */}
      {hasAutoSms && (
        <div className="flex items-center gap-1 mb-2 text-emerald-400 text-xs">
          <Zap className="h-3 w-3" />
          <span>Auto-SMS Sent</span>
        </div>
      )}
      
      <div 
        className="cursor-pointer"
        onClick={(e) => { e.stopPropagation(); onClick(lead); }}
      >
        <h4 className="font-medium text-slate-100 truncate mb-1">{lead.contact_name || "Unknown"}</h4>
        {lead.company && (
          <p className="text-xs text-slate-400 mb-1">{lead.company}</p>
        )}
        <p className="text-xs text-slate-400 mb-2 line-clamp-1">
          {SERVICE_TYPES.find(s => s.value === lead.service_type)?.label || lead.service_type || "General"}
        </p>
      </div>
      
      {/* Quick Action Buttons */}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-700/50">
        <div className="flex items-center gap-1">
          {lead.phone && (
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-7 w-7 p-0 text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10"
              onClick={(e) => { e.stopPropagation(); onQuickCall?.(lead); }}
            >
              <Phone className="h-3.5 w-3.5" />
            </Button>
          )}
          {lead.email && (
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-7 w-7 p-0 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10"
              onClick={(e) => { e.stopPropagation(); onQuickEmail?.(lead); }}
            >
              <Mail className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        <div className="text-xs text-slate-500">
          {decayStatus === "warning" && (
            <span className="text-yellow-500">{hoursSinceActivity}h</span>
          )}
          {decayStatus === "stagnant" && (
            <span className="text-red-400">{hoursSinceActivity}h</span>
          )}
        </div>
      </div>
    </div>
  );
});

// Lead Card with Status Dropdown (no drag-and-drop)
const LeadCardWithDropdown = memo(({ lead, onClick, onQuickCall, onQuickEmail, onStatusChange, onIcebox, onLost }) => {
  const decayStatus = getDecayStatus(lead);
  const decayClasses = getDecayClasses(decayStatus);
  const hoursSinceActivity = Math.round(getHoursSinceActivity(lead.last_activity_at || lead.created_at));
  const hasWakeUpTag = lead.tags?.includes("WAKE UP");
  const hasAutoIntake = lead.tags?.includes("AUTO-INTAKE");
  const hasAutoSms = lead.auto_sms_sent_at || lead.communications?.some(c => c.is_auto);
  
  const currentStatus = ALL_LEAD_STATUSES.find(s => s.value === getNormalizedStatus(lead.status)) || ALL_LEAD_STATUSES[0];

  const handleStatusSelect = (newStatus) => {
    if (newStatus === 'icebox') {
      onIcebox?.(lead);
    } else if (newStatus === 'lost') {
      onLost?.(lead);
    } else {
      onStatusChange?.(lead.lead_id, newStatus);
    }
  };

  return (
    <div 
      className={`p-3 rounded-lg shadow-sm transition-all mb-2 ${
        decayStatus === "stagnant" ? "bg-red-900/30" : "bg-slate-800"
      } ${decayClasses}`}
    >
      {/* Status Dropdown + Badges Row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1">
          {/* Status Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${currentStatus.color} text-white hover:opacity-90 transition-opacity`}>
                <ChevronDown className="h-3 w-3" />
                {currentStatus.label}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-slate-900 border-slate-700 min-w-[160px]">
              {ALL_LEAD_STATUSES.map(status => (
                <DropdownMenuItem 
                  key={status.value}
                  onClick={(e) => { e.stopPropagation(); handleStatusSelect(status.value); }}
                  className={`cursor-pointer hover:bg-slate-800 ${lead.status === status.value ? 'bg-slate-800' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${status.color}`}></div>
                    <span className={status.textColor}>{status.label}</span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          
          {decayStatus === "stagnant" && (
            <Badge className="bg-red-600 text-white text-xs px-1.5 py-0">
              <AlertOctagon className="h-3 w-3 mr-0.5" />
              STAGNANT
            </Badge>
          )}
          {hasWakeUpTag && (
            <Badge className="bg-cyan-500 text-white text-xs px-1.5 py-0">
              <Sparkles className="h-3 w-3 mr-0.5" />
              WAKE UP
            </Badge>
          )}
          {hasAutoIntake && (
            <Badge className="bg-purple-500 text-white text-xs px-1.5 py-0">
              <Bot className="h-3 w-3 mr-0.5" />
              AUTO
            </Badge>
          )}
        </div>
        {/* Priority Badge */}
        {lead.priority && lead.priority !== "undefined" && (
          <PriorityBadge priority={lead.priority} />
        )}
      </div>
      
      {/* Auto-SMS indicator */}
      {hasAutoSms && (
        <div className="flex items-center gap-1 mb-2 text-emerald-400 text-xs">
          <Zap className="h-3 w-3" />
          <span>Auto-SMS Sent</span>
        </div>
      )}
      
      <div 
        className="cursor-pointer"
        onClick={(e) => { e.stopPropagation(); onClick(lead); }}
      >
        <h4 className="font-medium text-slate-100 truncate mb-1">{lead.contact_name || "Unknown"}</h4>
        {lead.company && (
          <p className="text-xs text-slate-400 mb-1">{lead.company}</p>
        )}
        <p className="text-xs text-slate-400 mb-2 line-clamp-1">
          {lead.scope_of_work || lead.service_type || "No service specified"}
        </p>
      </div>
      
      {/* Quick Actions + Decay Timer */}
      <div className="flex items-center justify-between pt-2 border-t border-slate-700/50">
        <div className="flex items-center gap-1">
          {lead.phone && (
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-7 w-7 p-0 text-emerald-400 hover:bg-emerald-900/30"
              onClick={(e) => { e.stopPropagation(); onQuickCall(lead); }}
            >
              <Phone className="h-3.5 w-3.5" />
            </Button>
          )}
          {lead.email && (
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-7 w-7 p-0 text-blue-400 hover:bg-blue-900/30"
              onClick={(e) => { e.stopPropagation(); onQuickEmail(lead); }}
            >
              <Mail className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        <div className="text-xs text-slate-500">
          {decayStatus === "warning" && (
            <span className="text-yellow-500">{hoursSinceActivity}h</span>
          )}
          {decayStatus === "stagnant" && (
            <span className="text-red-400">{hoursSinceActivity}h</span>
          )}
        </div>
      </div>
    </div>
  );
});

// Static Lead Card (for drag overlay)
const LeadCardOverlay = memo(({ lead }) => {
  const decayStatus = getDecayStatus(lead);
  const decayClasses = getDecayClasses(decayStatus);
  
  return (
    <div className={`p-3 rounded-lg bg-slate-800 border-2 border-emerald-500 dragging-overlay ${decayClasses}`}>
      <h4 className="font-medium text-slate-100 truncate">{lead.contact_name}</h4>
      <p className="text-xs text-slate-400">{lead.phone}</p>
    </div>
  );
});

// Droppable Pipeline Column
const DroppablePipelineColumn = memo(({ stage, leads, onLeadClick, onQuickCall, onQuickEmail }) => {
  const stageConfig = PIPELINE_STAGES.find(s => s.value === stage) || PIPELINE_STAGES[0];
  const columnLeads = leads.filter(l => getNormalizedStatus(l.status) === stage && !l.is_frozen);
  
  const { isOver, setNodeRef } = useDroppable({
    id: `column-${stage}`,
    data: { stage }
  });
  
  return (
    <div 
      ref={setNodeRef}
      className={`flex-1 min-w-0 rounded-lg p-4 border-2 transition-all duration-150 ${
        isOver 
          ? 'border-emerald-400 bg-emerald-900/30 ring-2 ring-emerald-500/50 ring-offset-2 ring-offset-slate-950 scale-[1.02]' 
          : 'border-slate-700 bg-slate-900 hover:border-slate-600'
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${stageConfig.color}`} />
          <h3 className="font-semibold text-slate-100 text-sm uppercase tracking-wide">{stageConfig.label}</h3>
        </div>
        <Badge variant="outline" className="border-slate-600 text-slate-300 font-bold">{columnLeads.length}</Badge>
      </div>
      <ScrollArea className="h-[calc(100vh-420px)]">
        <div className="space-y-2 pr-2">
          {columnLeads.map(lead => (
            <DraggableLeadCard 
              key={lead.lead_id} 
              lead={lead} 
              onClick={onLeadClick}
              onQuickCall={onQuickCall}
              onQuickEmail={onQuickEmail}
            />
          ))}
          {columnLeads.length === 0 && (
            <p className="text-center text-slate-500 text-sm py-8">Drop leads here</p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
});

// Simple Pipeline Column with Dropdown Status (no drag-and-drop)
const SimplePipelineColumn = memo(({ stage, leads, onLeadClick, onQuickCall, onQuickEmail, onStatusChange, onIcebox, onLost }) => {
  const stageConfig = PIPELINE_STAGES.find(s => s.value === stage) || PIPELINE_STAGES[0];
  const columnLeads = leads.filter(l => getNormalizedStatus(l.status) === stage && !l.is_frozen);
  
  return (
    <div className="flex-1 min-w-0 rounded-lg p-4 border-2 border-slate-700 bg-slate-900 hover:border-slate-600">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${stageConfig.color}`} />
          <h3 className="font-semibold text-slate-100 text-sm uppercase tracking-wide">{stageConfig.label}</h3>
        </div>
        <Badge variant="outline" className="border-slate-600 text-slate-300 font-bold">{columnLeads.length}</Badge>
      </div>
      <ScrollArea className="h-[calc(100vh-420px)]">
        <div className="space-y-2 pr-2">
          {columnLeads.map(lead => (
            <LeadCardWithDropdown 
              key={lead.lead_id} 
              lead={lead} 
              onClick={onLeadClick}
              onQuickCall={onQuickCall}
              onQuickEmail={onQuickEmail}
              onStatusChange={onStatusChange}
              onIcebox={onIcebox}
              onLost={onLost}
            />
          ))}
          {columnLeads.length === 0 && (
            <p className="text-center text-slate-500 text-sm py-8">No leads</p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
});

// Won/Lost Drop Zones
const WonLostDropZones = memo(({ onDrop }) => {
  const { isOver: isOverLost, setNodeRef: setLostRef } = useDroppable({
    id: 'zone-lost',
    data: { zone: 'lost' }
  });
  
  const { isOver: isOverWon, setNodeRef: setWonRef } = useDroppable({
    id: 'zone-won',
    data: { zone: 'won' }
  });
  
  return (
    <div className="grid grid-cols-2 gap-4 mt-4">
      {/* Lost/Archive Zone */}
      <div 
        ref={setLostRef}
        className={`p-4 rounded-lg border-2 border-dashed transition-all duration-150 ${
          isOverLost 
            ? 'border-red-400 bg-red-900/40 ring-2 ring-red-500/50 ring-offset-2 ring-offset-slate-950 scale-[1.02]' 
            : 'border-red-700/50 bg-red-900/10 hover:border-red-600/70'
        }`}
      >
        <div className="flex items-center justify-center gap-2 text-red-400">
          <Archive className="h-5 w-5" />
          <span className="font-semibold uppercase tracking-wide text-sm">Archive / Lost</span>
        </div>
        <p className="text-center text-red-400/60 text-xs mt-1">Drop to mark as lost</p>
      </div>
      
      {/* Won/Sold Zone */}
      <div 
        ref={setWonRef}
        className={`p-4 rounded-lg border-2 border-dashed transition-all duration-150 ${
          isOverWon 
            ? 'border-emerald-400 bg-emerald-900/40 ring-2 ring-emerald-500/50 ring-offset-2 ring-offset-slate-950 scale-[1.02]' 
            : 'border-emerald-700/50 bg-emerald-900/10 hover:border-emerald-600/70'
        }`}
      >
        <div className="flex items-center justify-center gap-2 text-emerald-400">
          <Trophy className="h-5 w-5" />
          <span className="font-semibold uppercase tracking-wide text-sm">Sold / Won</span>
        </div>
        <p className="text-center text-emerald-400/60 text-xs mt-1">Drop to convert to project</p>
      </div>
    </div>
  );
});

// Icebox Drop Zone Component
const IceboxDropZone = memo(({ frozenLeads, onUnfreeze }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: 'zone-icebox',
    data: { zone: 'icebox' }
  });
  
  return (
    <div 
      ref={setNodeRef}
      className={`mt-4 p-4 rounded-lg border-2 border-dashed transition-all duration-150 ${
        isOver 
          ? 'border-cyan-400 bg-cyan-900/40 ring-2 ring-cyan-500/50 ring-offset-2 ring-offset-slate-950 scale-[1.01]' 
          : 'border-cyan-600/50 bg-cyan-900/20 hover:border-cyan-500/70'
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Snowflake className="h-5 w-5 text-cyan-400" />
          <h3 className="font-semibold text-cyan-300 uppercase tracking-wide">THE ICEBOX</h3>
          <span className="text-xs text-cyan-500">Frozen / Nurture Queue</span>
        </div>
        <Badge variant="outline" className="border-cyan-600 text-cyan-300">
          {frozenLeads.length} Frozen
        </Badge>
      </div>
      
      {frozenLeads.length > 0 ? (
        <div className="grid grid-cols-4 gap-3">
          {frozenLeads.map(lead => (
            <div 
              key={lead.lead_id}
              className="p-3 bg-cyan-900/30 border border-cyan-700/50 rounded-lg"
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-cyan-100 text-sm truncate">{lead.contact_name}</h4>
                <Snowflake className="h-4 w-4 text-cyan-400" />
              </div>
              <p className="text-xs text-cyan-400 mb-2">
                Wake up: {lead.frozen_until}
              </p>
              <Button 
                size="sm" 
                variant="outline"
                className="w-full border-cyan-600 text-cyan-300 hover:bg-cyan-800"
                onClick={() => onUnfreeze(lead.lead_id)}
              >
                <Sparkles className="h-3 w-3 mr-1" />
                Wake Now
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-cyan-500/60 text-sm py-4">
          {isOver ? "Release to freeze this lead!" : "Drag leads here to put them in nurture mode"}
        </p>
      )}
    </div>
  );
});

// Legacy LeadCard for non-drag contexts
const LeadCard = ({ lead, onClick }) => {
  const decayStatus = getDecayStatus(lead);
  const decayClasses = getDecayClasses(decayStatus);
  const hoursSinceActivity = Math.round(getHoursSinceActivity(lead.last_activity_at || lead.created_at));
  const hasWakeUpTag = lead.tags?.includes("WAKE UP");
  
  return (
    <div 
      onClick={() => onClick(lead)}
      className={`p-3 rounded-lg shadow-sm hover:shadow-lg cursor-pointer transition-all mb-2 ${
        decayStatus === "stagnant" ? "bg-red-900/30" : "bg-slate-800"
      } ${decayClasses}`}
    >
      {decayStatus === "stagnant" && (
        <div className="flex items-center gap-1 mb-2">
          <Badge className="bg-red-600 text-white text-xs px-1.5 py-0">
            <AlertOctagon className="h-3 w-3 mr-1" />
            STAGNANT
          </Badge>
          <span className="text-xs text-red-400">{hoursSinceActivity}h ago</span>
        </div>
      )}
      {hasWakeUpTag && (
        <Badge className="bg-cyan-500 text-white text-xs mb-2">
          <Sparkles className="h-3 w-3 mr-1" />
          WAKE UP
        </Badge>
      )}
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-medium text-slate-100 truncate">{lead.contact_name}</h4>
        {lead.priority && <PriorityBadge priority={lead.priority} />}
      </div>
      {lead.company && (
        <p className="text-xs text-slate-400 mb-1">{lead.company}</p>
      )}
      <p className="text-xs text-slate-400 mb-2 line-clamp-2">{lead.service_type}</p>
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <Phone className="h-3 w-3" />
        <span>{lead.phone}</span>
      </div>
      {lead.next_follow_up_date && (
        <div className="flex items-center gap-2 text-xs text-amber-500 mt-1">
          <Calendar className="h-3 w-3" />
          <span>Follow-up: {lead.next_follow_up_date}</span>
        </div>
      )}
      {decayStatus === "warning" && (
        <div className="flex items-center gap-1 mt-2 text-yellow-500 text-xs">
          <AlertTriangle className="h-3 w-3" />
          <span>{hoursSinceActivity}h since activity</span>
        </div>
      )}
    </div>
  );
};

// Pipeline Column (legacy, non-drag)
const PipelineColumn = ({ stage, leads, onLeadClick }) => {
  const stageConfig = PIPELINE_STAGES.find(s => s.value === stage) || PIPELINE_STAGES[0];
  const columnLeads = leads.filter(l => getNormalizedStatus(l.status) === stage);
  
  return (
    <div className="flex-1 min-w-0 bg-slate-900 rounded-lg p-4 border border-slate-700">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${stageConfig.color}`} />
          <h3 className="font-semibold text-slate-100 text-sm uppercase tracking-wide">{stageConfig.label}</h3>
        </div>
        <Badge variant="outline" className="border-slate-600 text-slate-300 font-bold">{columnLeads.length}</Badge>
      </div>
      <ScrollArea className="h-[calc(100vh-340px)]">
        <div className="space-y-2 pr-2">
          {columnLeads.map(lead => (
            <LeadCard key={lead.lead_id} lead={lead} onClick={onLeadClick} />
          ))}
          {columnLeads.length === 0 && (
            <p className="text-center text-slate-500 text-sm py-8">No leads</p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

// Communication Entry Component
const CommunicationEntry = ({ comm }) => (
  <div className="border-l-2 border-slate-700 pl-4 py-2">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="text-xs">
          {COMMUNICATION_TYPES.find(t => t.value === comm.comm_type)?.label || comm.comm_type}
        </Badge>
        <Badge variant="outline" className="text-xs">
          {comm.direction === "inbound" ? "← In" : "→ Out"}
        </Badge>
      </div>
      <span className="text-xs text-slate-500">
        {new Date(comm.created_at).toLocaleDateString()} {new Date(comm.created_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
      </span>
    </div>
    <p className="text-sm text-slate-700 mt-1">{comm.summary}</p>
    {comm.next_step && (
      <p className="text-xs text-slate-500 mt-1">Next: {comm.next_step}</p>
    )}
    <p className="text-xs text-slate-400 mt-1">by {comm.owner_name}</p>
  </div>
);

// Lead Detail Modal
const LeadDetailModal = ({ lead, isOpen, onClose, onUpdate, teamMembers }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("details");
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(lead || {});
  const [loading, setLoading] = useState(false);
  const [commData, setCommData] = useState({ comm_type: "call", direction: "outbound", summary: "", next_step: "", follow_up_date: "" });
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [lostNotes, setLostNotes] = useState("");
  const [showLostDialog, setShowLostDialog] = useState(false);
  const [apptData, setApptData] = useState({ appointment_type: "site_visit", scheduled_date: "", scheduled_time: "", location: "", notes: "" });
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [inRouteMinutes, setInRouteMinutes] = useState(60);

  const isOfficeUser = ["owner", "office_manager", "project_manager", "admin"].includes(
    user?.role || ""
  );

  const isLeadAtRisk = (appt, minutes) => {
    if (!appt?.scheduled_start) return false;
    const thresholdMinutes = minutes ?? 60;
    const start = new Date(appt.scheduled_start).getTime();
    const now = Date.now();
    const thresholdMs = thresholdMinutes * 60 * 1000;
    return now >= start - thresholdMs;
  };

  const refreshAppointments = useCallback(async () => {
    if (!lead?.lead_id) return;
    setAppointmentsLoading(true);
    try {
      const [settingsRes, apptRes] = await Promise.all([
        API.get("/settings"),
        API.get("/appointments"),
      ]);
      const value = settingsRes.data?.in_route_required_lead_time_minutes;
      setInRouteMinutes(typeof value === "number" && value > 0 ? value : 60);
      const now = new Date();
      const end = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
      const filtered = (Array.isArray(apptRes.data) ? apptRes.data : []).filter((a) => {
        if (a.lead_id !== lead.lead_id || !a.scheduled_start) return false;
        const t = new Date(a.scheduled_start).getTime();
        return t >= now.getTime() && t <= end.getTime();
      });
      filtered.sort(
        (a, b) =>
          new Date(a.scheduled_start || 0).getTime() -
          new Date(b.scheduled_start || 0).getTime()
      );
      setAppointments(filtered);
    } catch (error) {
      // non-fatal
    } finally {
      setAppointmentsLoading(false);
    }
  }, [lead?.lead_id]);

  useEffect(() => {
    if (lead) {
      setFormData(lead);
      setAiSummary(null);
    }
  }, [lead]);

  useEffect(() => {
    if (isOpen) {
      refreshAppointments();
    }
  }, [isOpen, refreshAppointments]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await API.put(`/leads/${lead.lead_id}`, formData);
      toast.success("Lead updated");
      setEditMode(false);
      onUpdate();
    } catch (error) {
      toast.error("Failed to update lead");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === "lost") {
      setShowLostDialog(true);
      return;
    }
    
    setLoading(true);
    try {
      await API.post(`/leads/${lead.lead_id}/status`, { status: newStatus });
      toast.success(`Status updated to ${LEAD_STATUSES.find(s => s.value === newStatus)?.label}`);
      onUpdate();
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const handleLostSubmit = async () => {
    if (!lostReason) {
      toast.error("Please select a reason");
      return;
    }
    setLoading(true);
    try {
      await API.post(`/leads/${lead.lead_id}/status`, { 
        status: "lost", 
        lost_reason: lostReason,
        lost_notes: lostNotes 
      });
      toast.success("Lead marked as lost");
      setShowLostDialog(false);
      onUpdate();
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCommunication = async () => {
    if (!commData.summary) {
      toast.error("Please add a summary");
      return;
    }
    setLoading(true);
    try {
      await API.post(`/leads/${lead.lead_id}/communications`, commData);
      toast.success("Communication logged");
      setCommData({ comm_type: "call", direction: "outbound", summary: "", next_step: "", follow_up_date: "" });
      onUpdate();
    } catch (error) {
      toast.error("Failed to log communication");
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleAppointment = async () => {
    if (!apptData.scheduled_date || !apptData.scheduled_time) {
      toast.error("Please select date and time");
      return;
    }
    setLoading(true);
    try {
      await API.post(`/leads/${lead.lead_id}/appointments`, apptData);
      toast.success("Appointment scheduled");
      setApptData({ appointment_type: "site_visit", scheduled_date: "", scheduled_time: "", location: "", notes: "" });
      onUpdate();
    } catch (error) {
      toast.error("Failed to schedule appointment");
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = async () => {
    setLoading(true);
    try {
      const result = await API.post(`/leads/${lead.lead_id}/convert`, {
        project_name: `${lead.contact_name} - ${lead.service_type}`
      });
      toast.success("Lead converted to client and project!");
      onUpdate();
      onClose();
    } catch (error) {
      toast.error("Failed to convert lead");
    } finally {
      setLoading(false);
    }
  };

  const loadAiSummary = async () => {
    setAiLoading(true);
    try {
      const response = await API.get(`/leads/${lead.lead_id}/ai-summary`);
      setAiSummary(response.data.summary);
    } catch (error) {
      toast.error("Failed to generate AI summary");
    } finally {
      setAiLoading(false);
    }
  };

  if (!lead) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-xl">{lead.contact_name}</DialogTitle>
                {lead.company && <p className="text-sm text-slate-500">{lead.company}</p>}
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={lead.status} />
                <PriorityBadge priority={lead.priority} />
              </div>
            </div>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="communications">Communications</TabsTrigger>
              <TabsTrigger value="appointments">Appointments</TabsTrigger>
              <TabsTrigger value="actions">Actions</TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1 mt-4">
              {/* Details Tab */}
              <TabsContent value="details" className="mt-0 space-y-4">
                {/* AI Summary Section */}
                <Card className="bg-gradient-to-r from-slate-800 to-slate-700 text-white">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Sparkles className="h-4 w-4" />
                        AI Lead Summary
                      </CardTitle>
                      <Button 
                        size="sm" 
                        variant="secondary" 
                        onClick={loadAiSummary}
                        disabled={aiLoading}
                      >
                        {aiLoading ? <RefreshCw className="h-3 w-3 animate-spin" /> : "Generate"}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {aiSummary ? (
                      <div className="text-sm whitespace-pre-wrap text-slate-200">{aiSummary}</div>
                    ) : (
                      <p className="text-sm text-slate-400">Click Generate to get an AI summary of this lead</p>
                    )}
                  </CardContent>
                </Card>

                {/* Contact Info */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Contact Information</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-slate-400" />
                      <span>{lead.phone}</span>
                    </div>
                    {lead.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-slate-400" />
                        <span>{lead.email}</span>
                      </div>
                    )}
                    {lead.service_address && (
                      <div className="flex items-center gap-2 col-span-2">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        <span>{lead.service_address}, {lead.city} {lead.state} {lead.zip_code}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Service Details */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Service Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">Service Type</Label>
                        <p className="font-medium">{SERVICE_TYPES.find(s => s.value === lead.service_type)?.label || lead.service_type}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Budget Range</Label>
                        <p className="font-medium">${lead.estimated_budget_min?.toLocaleString()} - ${lead.estimated_budget_max?.toLocaleString()}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Urgency</Label>
                        <p className="font-medium">{lead.urgency === "asap" ? "ASAP" : lead.urgency === "30_days" ? "Within 30 Days" : "60+ Days"}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Source</Label>
                        <p className="font-medium">{LEAD_SOURCES.find(s => s.value === lead.lead_source_id)?.label || lead.lead_source_id}</p>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Description</Label>
                      <p className="text-sm">{lead.description}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Tracking */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Tracking</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Last Contacted</Label>
                      <p className="font-medium">{lead.last_contacted ? new Date(lead.last_contacted).toLocaleDateString() : "Never"}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Next Follow-Up</Label>
                      <p className="font-medium">{lead.next_follow_up_date || "Not scheduled"}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Next Action</Label>
                      <p className="font-medium">{NEXT_ACTIONS.find(a => a.value === lead.next_action)?.label || lead.next_action}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Assigned To</Label>
                      <p className="font-medium">{lead.assigned_name || "Unassigned"}</p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Communications Tab */}
              <TabsContent value="communications" className="mt-0 space-y-4">
                {/* Quick Log Buttons */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Log Communication</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Type</Label>
                        <Select value={commData.comm_type} onValueChange={(v) => setCommData({...commData, comm_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {COMMUNICATION_TYPES.map(t => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Direction</Label>
                        <Select value={commData.direction} onValueChange={(v) => setCommData({...commData, direction: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="outbound">Outbound (You called)</SelectItem>
                            <SelectItem value="inbound">Inbound (They called)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Summary</Label>
                      <Textarea 
                        value={commData.summary} 
                        onChange={(e) => setCommData({...commData, summary: e.target.value})}
                        placeholder="What was discussed?"
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Next Step</Label>
                        <Select value={commData.next_step || ""} onValueChange={(v) => setCommData({...commData, next_step: v})}>
                          <SelectTrigger><SelectValue placeholder="Select next step" /></SelectTrigger>
                          <SelectContent>
                            {NEXT_ACTIONS.map(a => (
                              <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Follow-Up Date</Label>
                        <Input 
                          type="date" 
                          value={commData.follow_up_date || ""} 
                          onChange={(e) => setCommData({...commData, follow_up_date: e.target.value})}
                        />
                      </div>
                    </div>
                    <Button onClick={handleAddCommunication} disabled={loading} className="w-full">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Log Communication
                    </Button>
                  </CardContent>
                </Card>

                {/* Communication History */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Communication History ({lead.communications?.length || 0})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {lead.communications?.length > 0 ? (
                      <div className="space-y-3">
                        {[...lead.communications].reverse().map((comm, idx) => (
                          <CommunicationEntry key={idx} comm={comm} />
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-slate-400 py-4">No communications logged yet</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Appointments Tab */}
              <TabsContent value="appointments" className="mt-0 space-y-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Schedule Appointment</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Type</Label>
                        <Select value={apptData.appointment_type} onValueChange={(v) => setApptData({...apptData, appointment_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="site_visit">Site Visit</SelectItem>
                            <SelectItem value="call">Phone Call</SelectItem>
                            <SelectItem value="meeting">Meeting</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Location</Label>
                        <Input 
                          value={apptData.location || ""} 
                          onChange={(e) => setApptData({...apptData, location: e.target.value})}
                          placeholder="Address or 'Phone'"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Date</Label>
                        <Input 
                          type="date" 
                          value={apptData.scheduled_date} 
                          onChange={(e) => setApptData({...apptData, scheduled_date: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Time</Label>
                        <Input 
                          type="time" 
                          value={apptData.scheduled_time} 
                          onChange={(e) => setApptData({...apptData, scheduled_time: e.target.value})}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Notes</Label>
                      <Textarea 
                        value={apptData.notes || ""} 
                        onChange={(e) => setApptData({...apptData, notes: e.target.value})}
                        placeholder="Additional notes"
                        rows={2}
                      />
                    </div>
                    <Button onClick={handleScheduleAppointment} disabled={loading} className="w-full">
                      <Calendar className="h-4 w-4 mr-2" />
                      Schedule Appointment
                    </Button>
                  </CardContent>
                </Card>

                {/* Appointment List */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Scheduled Appointments ({lead.appointments?.length || 0})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {lead.appointments?.length > 0 ? (
                      <div className="space-y-3">
                        {lead.appointments.map((appt, idx) => (
                          <div key={idx} className="p-3 bg-slate-800 border border-slate-700 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-slate-400" />
                                <span className="font-medium">{appt.scheduled_date} at {appt.scheduled_time}</span>
                              </div>
                              <Badge variant={appt.status === "completed" ? "default" : appt.status === "cancelled" ? "destructive" : "outline"}>
                                {appt.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-400 mt-1">{appt.appointment_type} - {appt.location || "TBD"}</p>
                            {appt.notes && <p className="text-xs text-slate-500 mt-1">{appt.notes}</p>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-slate-500 py-4">No appointments scheduled</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Actions Tab */}
              <TabsContent value="actions" className="mt-0 space-y-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Change Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-2">
                      {LEAD_STATUSES.filter(s => s.value !== lead.status).map(status => (
                        <Button 
                          key={status.value}
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(status.value)}
                          disabled={loading}
                          className="justify-start"
                        >
                          <div className={`w-2 h-2 rounded-full ${status.color} mr-2`} />
                          {status.label}
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {lead.status !== "won" && lead.status !== "lost" && (
                  <Card className="border-emerald-200 bg-emerald-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-emerald-700">Convert to Client & Project</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-emerald-600 mb-3">
                        This will create a new Client record and a linked Project, preserving all lead details.
                      </p>
                      <Button onClick={handleConvert} disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Convert Lead (Mark as Won)
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* THE ICEBOX - Freeze Lead */}
                {lead.status !== "won" && lead.status !== "lost" && !lead.is_frozen && (
                  <Card className="border-cyan-600/50 bg-cyan-900/20">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-cyan-300 flex items-center gap-2">
                        <Snowflake className="h-4 w-4" />
                        Send to Icebox
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-cyan-400/80 mb-3">
                        Freeze this lead and set a wake-up date for future follow-up.
                      </p>
                      <Button 
                        onClick={() => {
                          onClose();
                          // Trigger parent's icebox modal
                          window.dispatchEvent(new CustomEvent('openIceboxModal', { detail: lead }));
                        }} 
                        variant="outline"
                        className="w-full border-cyan-600 text-cyan-300 hover:bg-cyan-800"
                      >
                        <Snowflake className="h-4 w-4 mr-2" />
                        Freeze Lead
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {lead.status === "won" && (
                  <Card className="border-emerald-200 bg-emerald-50">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 text-emerald-700">
                        <CheckCircle className="h-5 w-5" />
                        <span className="font-medium">Lead Converted</span>
                      </div>
                      {lead.converted_client_id && (
                        <p className="text-sm text-emerald-600 mt-2">Client ID: {lead.converted_client_id}</p>
                      )}
                      {lead.converted_project_id && (
                        <p className="text-sm text-emerald-600">Project ID: {lead.converted_project_id}</p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {lead.status === "lost" && (
                  <Card className="border-red-200 bg-red-50">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 text-red-700">
                        <XCircle className="h-5 w-5" />
                        <span className="font-medium">Lead Lost</span>
                      </div>
                      <p className="text-sm text-red-600 mt-2">
                        Reason: {LOST_REASONS.find(r => r.value === lead.lost_reason)?.label || lead.lost_reason}
                      </p>
                      {lead.lost_notes && (
                        <p className="text-sm text-red-500 mt-1">{lead.lost_notes}</p>
                      )}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Lost Reason Dialog */}
      <Dialog open={showLostDialog} onOpenChange={setShowLostDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Lead as Lost</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason (Required)</Label>
              <Select value={lostReason} onValueChange={setLostReason}>
                <SelectTrigger><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent>
                  {LOST_REASONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea 
                value={lostNotes} 
                onChange={(e) => setLostNotes(e.target.value)}
                placeholder="Any additional details..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLostDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleLostSubmit} disabled={loading}>
              Mark as Lost
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Create Lead Modal
const CreateLeadModal = ({ isOpen, onClose, onSuccess, teamMembers, onDuplicate }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    contact_name: "",
    phone: "",
    email: "",
    company: "",
    service_address: "",
    city: "",
    state: "",
    zip_code: "",
    service_type: "general_contracting",
    description: "",
    estimated_budget_min: "",
    estimated_budget_max: "",
    desired_start_date: "",
    urgency: "30_days",
    lead_source_id: "",
    priority: "medium",
    assigned_to: "",
    notes: ""
  });
  const [loading, setLoading] = useState(false);
  const [leadSources, setLeadSources] = useState([]);
  const [leadSourcesLoading, setLeadSourcesLoading] = useState(false);
  const [showAppointmentPrompt, setShowAppointmentPrompt] = useState(false);
  const [createdLead, setCreatedLead] = useState(null);

  useEffect(() => {
    const fetchLeadSources = async () => {
      setLeadSourcesLoading(true);
      try {
        const res = await API.get("/lead-sources", { params: { active: true } });
        const sources = Array.isArray(res.data) ? res.data : [];
        setLeadSources(sources);
        setFormData(prev => prev.lead_source_id ? prev : (sources.length > 0 ? { ...prev, lead_source_id: sources[0].lead_source_id } : prev));
      } catch (error) {
        toast.error("Failed to load lead sources");
      } finally {
        setLeadSourcesLoading(false);
      }
    };
    if (isOpen) {
      fetchLeadSources();
    }
  }, [isOpen]);

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowAppointmentPrompt(false);
      setCreatedLead(null);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!formData.contact_name || !formData.phone || !formData.description) {
      toast.error("Please fill in required fields: Name, Phone, Description");
      return;
    }
    if (!formData.lead_source_id) {
      toast.error("No active Lead Sources found. Ask an owner/office manager to create one in Settings.");
      return;
    }
    setLoading(true);
    try {
      // Clean up form data - convert empty strings to proper defaults
      const cleanedData = {
        ...formData,
        estimated_budget_min: formData.estimated_budget_min ? parseFloat(formData.estimated_budget_min) : 0,
        estimated_budget_max: formData.estimated_budget_max ? parseFloat(formData.estimated_budget_max) : 0,
      };
      const response = await API.post("/leads", cleanedData);
      const newLead = response.data;
      toast.success("Lead created successfully");
      onSuccess();
      
      // Store lead info for appointment prompt
      setCreatedLead({
        lead_id: newLead.lead_id,
        contact_name: formData.contact_name,
        phone: formData.phone,
        service_address: formData.service_address,
        city: formData.city,
        state: formData.state
      });
      setShowAppointmentPrompt(true);
      
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      if (status === 409 && data?.duplicate_lead_id && onDuplicate) {
        onDuplicate({ payload: formData, duplicateLeadId: data.duplicate_lead_id });
      } else {
        toast.error("Failed to create lead");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleAppointment = () => {
    if (createdLead) {
      // Navigate to calendar with lead pre-selected
      const leadLabel = `${createdLead.contact_name}${createdLead.phone ? ' — ' + createdLead.phone : ''}`;
      navigate(`/appointments?lead_id=${createdLead.lead_id}&lead_label=${encodeURIComponent(leadLabel)}&create=1`);
    }
    handleCloseAll();
  };

  const handleSkipAppointment = () => {
    handleCloseAll();
  };

  const handleCloseAll = () => {
    setShowAppointmentPrompt(false);
    setCreatedLead(null);
    setFormData({
      contact_name: "", phone: "", email: "", company: "", service_address: "",
      city: "", state: "", zip_code: "", service_type: "general_contracting",
      description: "", estimated_budget_min: "", estimated_budget_max: "",
      desired_start_date: "", urgency: "30_days", lead_source_id: "",
      priority: "medium", assigned_to: "", notes: ""
    });
    onClose();
  };

  // Show appointment prompt dialog
  if (showAppointmentPrompt && createdLead) {
    return (
      <Dialog open={true} onOpenChange={handleSkipAppointment}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-emerald-500" />
              Schedule Appointment?
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-slate-300 mb-4">
              Lead <span className="font-semibold text-emerald-400">{createdLead.contact_name}</span> was created successfully.
            </p>
            <p className="text-slate-400 text-sm">
              Would you like to schedule an appointment with this lead now?
            </p>
          </div>

          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={handleSkipAppointment}>
              Skip for Now
            </Button>
            <Button onClick={handleScheduleAppointment} className="bg-emerald-600 hover:bg-emerald-500">
              <Calendar className="h-4 w-4 mr-2" />
              Schedule Appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Lead</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Contact Name *</Label>
              <Input 
                value={formData.contact_name}
                onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                placeholder="John Smith"
              />
            </div>
            <div>
              <Label>Company</Label>
              <Input 
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
                placeholder="Optional"
              />
            </div>
            <div>
              <Label>Phone *</Label>
              <Input 
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="555-123-4567"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input 
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="john@example.com"
              />
            </div>
          </div>

          {/* Address */}
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-2">
              <Label>Service Address</Label>
              <Input 
                value={formData.service_address}
                onChange={(e) => setFormData({...formData, service_address: e.target.value})}
                placeholder="123 Main St"
              />
            </div>
            <div>
              <Label>City</Label>
              <Input 
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>State</Label>
                <Input 
                  value={formData.state}
                  onChange={(e) => setFormData({...formData, state: e.target.value})}
                  maxLength={2}
                />
              </div>
              <div>
                <Label>Zip</Label>
                <Input 
                  value={formData.zip_code}
                  onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                />
              </div>
            </div>
          </div>

          {/* Service Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Service Type</Label>
              <Select value={formData.service_type} onValueChange={(v) => setFormData({...formData, service_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Lead Source</Label>
              <Select value={formData.lead_source_id} onValueChange={(v) => setFormData({...formData, lead_source_id: v})}>
                <SelectTrigger><SelectValue placeholder={leadSourcesLoading ? "Loading..." : (leadSources.length ? "Select a source" : "No active sources")} /></SelectTrigger>
                <SelectContent>
                  {leadSources.map(s => (
                    <SelectItem key={s.lead_source_id} value={s.lead_source_id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {leadSources.length === 0 && !leadSourcesLoading && (
                <p className="text-xs text-amber-400 mt-1">
                  No active Lead Sources found. Ask an owner/office manager to create one in Settings.
                </p>
              )}
            </div>
          </div>

          <div>
            <Label>Description of Request *</Label>
            <Textarea 
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Describe what the customer needs..."
              rows={3}
            />
          </div>

          {/* Budget & Timing */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label>Budget Min</Label>
              <Input 
                type="number"
                value={formData.estimated_budget_min}
                onChange={(e) => setFormData({
                  ...formData,
                  estimated_budget_min: e.target.value,
                })}
                placeholder="Min"
              />
            </div>
            <div>
              <Label>Budget Max</Label>
              <Input 
                type="number"
                value={formData.estimated_budget_max}
                onChange={(e) => setFormData({
                  ...formData,
                  estimated_budget_max: e.target.value,
                })}
                placeholder="Max"
              />
            </div>
            <div>
              <Label>Desired Start</Label>
              <Input 
                type="date"
                value={formData.desired_start_date}
                onChange={(e) => setFormData({...formData, desired_start_date: e.target.value})}
              />
            </div>
            <div>
              <Label>How Soon?</Label>
              <Select value={formData.urgency} onValueChange={(v) => setFormData({...formData, urgency: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="asap">ASAP</SelectItem>
                  <SelectItem value="30_days">Within 30 Days</SelectItem>
                  <SelectItem value="60_plus_days">60+ Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Assignment & Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Priority</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assign To</Label>
              <Select value={formData.assigned_to || "unassigned"} onValueChange={(v) => setFormData({...formData, assigned_to: v === "unassigned" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="Select team member" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {teamMembers.map(m => (
                    <SelectItem key={m.user_id || m.member_id} value={m.user_id || m.member_id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea 
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Any additional notes..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || leadSourcesLoading || leadSources.length === 0 || !formData.lead_source_id}>
            <UserPlus className="h-4 w-4 mr-2" />
            Create Lead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
const LeadsClientsPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { can } = usePermissions();

  const [activeTab, setActiveTab] = useState("leads");
  const [viewMode, setViewMode] = useState("pipeline"); // pipeline or list
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, payload: null, duplicateLeadId: null });
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  
  // CRM Advanced Features State
  const [frozenLeads, setFrozenLeads] = useState([]);
  const [showIceboxModal, setShowIceboxModal] = useState(false);
  const [leadToFreeze, setLeadToFreeze] = useState(null);
  const [wakeUpDate, setWakeUpDate] = useState("");
  const [showLostReasonModal, setShowLostReasonModal] = useState(false);
  const [leadToMarkLost, setLeadToMarkLost] = useState(null);
  const [selectedLostReason, setSelectedLostReason] = useState("");
  const [lostNotes, setLostNotes] = useState("");
  
  // Drag and Drop State
  const [activeDragLead, setActiveDragLead] = useState(null);
  const [isDraggingPipeline, setIsDraggingPipeline] = useState(false);
  const [showWonModal, setShowWonModal] = useState(false);
  const [leadToWin, setLeadToWin] = useState(null);
  
  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  const loadData = useCallback(async () => {
    try {
      const [leadsRes, clientsRes, teamRes, statsRes, frozenRes] = await Promise.all([
        API.get("/leads"),
        API.get("/clients"),
        API.get("/team"),
        API.get("/leads/dashboard/stats"),
        API.get("/leads/frozen").catch(() => ({ data: [] }))
      ]);
      setLeads(leadsRes.data);
      setClients(clientsRes.data);
      setTeamMembers(teamRes.data);
      setDashboardStats(statsRes.data);
      setFrozenLeads(frozenRes.data || []);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Check for wake-ups on load
    API.post("/leads/check-wakeups").catch(() => {});
  }, [loadData]);
  
  // Listen for icebox modal event from LeadDetailModal
  useEffect(() => {
    const handleIceboxEvent = (e) => {
      openIceboxModal(e.detail);
    };
    window.addEventListener('openIceboxModal', handleIceboxEvent);
    return () => window.removeEventListener('openIceboxModal', handleIceboxEvent);
  }, []);
  
  // Deep-link: /leads?lead_id=...
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const leadId = params.get("lead_id");
    if (!leadId || leads.length === 0) return;
    const match = leads.find((l) => l.lead_id === leadId);
    if (match) {
      setActiveTab("leads");
      setSelectedLead(match);
    }
  }, [location.search, leads]);
  
  // Filter out frozen leads from main list
  const activeLeads = leads.filter(l => !l.is_frozen);

  const filteredLeads = activeLeads.filter(lead => {
    const matchesSearch = !searchQuery || 
      lead.contact_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.phone?.includes(searchQuery);
    // Use normalized status for filtering to match both legacy and new statuses
    const normalizedLeadStatus = getNormalizedStatus(lead.status);
    const matchesStatus = !filterStatus || normalizedLeadStatus === filterStatus || lead.status === filterStatus;
    return matchesSearch && matchesStatus;
  });
  
  // ================== CRM FEATURE HANDLERS ==================
  
  // Handle freezing a lead (Icebox)
  const handleFreezeLead = async () => {
    if (!leadToFreeze || !wakeUpDate) {
      toast.error("Please select a wake-up date");
      return;
    }
    try {
      await API.post(`/leads/${leadToFreeze.lead_id}/freeze`, { wake_up_date: wakeUpDate });
      toast.success(`Lead frozen until ${wakeUpDate}`);
      setShowIceboxModal(false);
      setLeadToFreeze(null);
      setWakeUpDate("");
      loadData();
    } catch (error) {
      toast.error("Failed to freeze lead");
    }
  };
  
  // Handle unfreezing a lead (wake up)
  const handleUnfreezeLead = async (leadId) => {
    try {
      await API.post(`/leads/${leadId}/unfreeze`);
      toast.success("Lead awakened and moved to New Lead!");
      loadData();
    } catch (error) {
      toast.error("Failed to unfreeze lead");
    }
  };
  
  // Handle marking lead as lost with reason (The Why Button)
  const handleMarkLost = async () => {
    if (!leadToMarkLost || !selectedLostReason) {
      toast.error("Please select a reason for loss");
      return;
    }
    try {
      await API.post(`/leads/${leadToMarkLost.lead_id}/lost-reason`, {
        lost_reason: selectedLostReason,
        lost_notes: lostNotes
      });
      toast.success("Lead marked as lost");
      setShowLostReasonModal(false);
      setLeadToMarkLost(null);
      setSelectedLostReason("");
      setLostNotes("");
      loadData();
    } catch (error) {
      toast.error("Failed to mark lead as lost");
    }
  };
  
  // Open lost reason modal
  const openLostReasonModal = (lead) => {
    setLeadToMarkLost(lead);
    setSelectedLostReason("");
    setLostNotes("");
    setShowLostReasonModal(true);
  };
  
  // Open icebox modal
  const openIceboxModal = (lead) => {
    setLeadToFreeze(lead);
    setWakeUpDate("");
    setShowIceboxModal(true);
  };
  
  // Update activity when card is moved/clicked
  const handleLeadActivity = async (lead) => {
    try {
      await API.post(`/leads/${lead.lead_id}/activity`);
    } catch (error) {
      console.error("Failed to update activity");
    }
  };

  // Handle status change from dropdown
  const handleStatusChange = async (leadId, newStatus) => {
    const lead = leads.find(l => l.lead_id === leadId);
    if (!lead) return;
    
    const previousStatus = lead.status;
    const previousActivityAt = lead.last_activity_at;
    
    if (getNormalizedStatus(previousStatus) === newStatus) return;
    
    // OPTIMISTIC UPDATE: Immediately update local state
    const optimisticTimestamp = new Date().toISOString();
    setLeads(prevLeads => 
      prevLeads.map(l => 
        l.lead_id === leadId 
          ? { ...l, status: newStatus, last_activity_at: optimisticTimestamp, is_frozen: false } 
          : l
      )
    );
    
    const statusLabel = ALL_LEAD_STATUSES.find(s => s.value === newStatus)?.label || newStatus;
    toast.success(`Lead moved to ${statusLabel}`);
    
    // Fire confetti for won leads
    if (newStatus === 'won') {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
    
    // Background API call - on failure, revert to previous status
    try {
      await API.post(`/leads/${leadId}/status`, { status: newStatus });
      await API.post(`/leads/${leadId}/activity`);
    } catch (error) {
      const serverMessage = error?.response?.data?.detail || "Failed to update lead status";
      toast.error(serverMessage);
      // Revert
      setLeads(prevLeads => 
        prevLeads.map(l => 
          l.lead_id === leadId 
            ? { ...l, status: previousStatus, last_activity_at: previousActivityAt }
            : l
        )
      );
    }
  };
  
  // Seed test data for Visual Decay demo
  const seedTestData = async () => {
    try {
      const res = await API.post("/leads/seed-test-data");
      toast.success("Test leads created!");
      loadData();
    } catch (error) {
      toast.error("Failed to create test leads");
    }
  };
  
  // ================== DRAG AND DROP HANDLERS ==================
  
  const handleDragStart = (event) => {
    const { active } = event;
    const lead = leads.find(l => l.lead_id === active.id);
    setActiveDragLead(lead);
    setIsDraggingPipeline(true);
  };
  
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDragLead(null);
    setIsDraggingPipeline(false);
    
    if (!over) return;
    
    const leadId = active.id;
    const lead = leads.find(l => l.lead_id === leadId);
    if (!lead) return;
    
    const targetId = over.id;
    
    // Handle drop on Won zone
    if (targetId === 'zone-won') {
      setLeadToWin(lead);
      setShowWonModal(true);
      // Fire confetti!
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      return;
    }
    
    // Handle drop on Lost zone
    if (targetId === 'zone-lost') {
      openLostReasonModal(lead);
      return;
    }
    
    // Handle drop on Icebox zone
    if (targetId === 'zone-icebox') {
      openIceboxModal(lead);
      return;
    }
    
    // Handle drop on pipeline column
    if (targetId.startsWith('column-')) {
      const newStatus = targetId.replace('column-', '');
      const previousStatus = lead.status;
      const previousActivityAt = lead.last_activity_at;

      if (getNormalizedStatus(previousStatus) !== newStatus) {
        // OPTIMISTIC UPDATE: Immediately update local state
        const optimisticTimestamp = new Date().toISOString();
        setLeads(prevLeads => 
          prevLeads.map(l => 
            l.lead_id === leadId 
              ? { ...l, status: newStatus, last_activity_at: optimisticTimestamp } 
              : l
          )
        );
        
        toast.success(`Lead moved to ${PIPELINE_STAGES.find(s => s.value === newStatus)?.label}`);
        
        // Background API call - on failure, revert to previous status
        API.post(`/leads/${leadId}/status`, { status: newStatus })
          .then(() => API.post(`/leads/${leadId}/activity`))
          .catch((error) => {
            const serverMessage = error?.response?.data?.detail || "Failed to update lead status";
            toast.error(serverMessage);
            // Revert ONLY if the lead is still in the status this request tried to set
            setLeads(prevLeads => 
              prevLeads.map(l => 
                l.lead_id === leadId 
                  ? (
                      getNormalizedStatus(l.status) === newStatus
                        ? {
                            ...l,
                            status: previousStatus,
                            last_activity_at: previousActivityAt
                          }
                        : l
                    )
                  : l
              )
            );
          });
      }
    }
  };
  
  // Handle Won conversion
  const handleWonConvert = async () => {
    if (!leadToWin) return;
    try {
      await API.post(`/leads/${leadToWin.lead_id}/convert`, {
        project_name: `${leadToWin.contact_name} - ${leadToWin.service_type}`
      });
      toast.success("🎉 Lead converted to client and project!");
      confetti({
        particleCount: 200,
        spread: 100,
        origin: { y: 0.5 }
      });
      setShowWonModal(false);
      setLeadToWin(null);
      loadData();
    } catch (error) {
      toast.error("Failed to convert lead");
    }
  };
  
  // Quick action handlers
  const handleQuickCall = (lead) => {
    if (lead.phone) {
      window.open(`tel:${lead.phone}`, '_self');
      toast.info(`Calling ${lead.contact_name}...`);
    }
  };
  
  const handleQuickEmail = (lead) => {
    if (lead.email) {
      window.open(`mailto:${lead.email}`, '_blank');
      toast.info(`Opening email to ${lead.contact_name}...`);
    }
  };
  
  // ================== OMNI-CHANNEL SIMULATION ==================
  
  const simulateEmailLead = async () => {
    try {
      const res = await API.post("/demo/simulate-email-lead");
      toast.success(`📧 Email lead received: ${res.data.lead_name}`);
      loadData();
      
      // Simulate auto-SMS after delay
      setTimeout(async () => {
        try {
          await API.post(`/leads/${res.data.lead_id}/send-auto-sms`);
          toast.success(`📱 Auto-SMS sent to ${res.data.lead_name}!`);
          loadData();
        } catch (e) {
          console.error("Auto-SMS failed");
        }
      }, 5000); // 5 second delay for demo
    } catch (error) {
      toast.error("Failed to simulate email lead");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-chivo font-bold text-white tracking-tight">LEADS & CLIENTS</h1>
          <p className="text-slate-500 font-ibm mt-1">Manage your sales pipeline and customer relationships</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
          <UserPlus className="h-4 w-4 mr-2" />
          New Lead
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="leads" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Leads ({leads.filter(l => !["won", "lost"].includes(l.status)).length})
          </TabsTrigger>
          <TabsTrigger value="clients" className="flex items-center gap-2">
            <Building className="h-4 w-4" />
            Clients ({clients.length})
          </TabsTrigger>
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Dashboard
          </TabsTrigger>
        </TabsList>

        {/* Leads Tab */}
        <TabsContent value="leads" className="mt-4">
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input 
                  placeholder="Search leads..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {LEAD_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant={viewMode === "pipeline" ? "default" : "outline"} 
                size="sm"
                onClick={() => setViewMode("pipeline")}
              >
                Pipeline
              </Button>
              <Button 
                variant={viewMode === "list" ? "default" : "outline"} 
                size="sm"
                onClick={() => setViewMode("list")}
              >
                List
              </Button>
            </div>
          </div>

          {/* Pipeline View - 4 Main Columns with Status Dropdown */}
          {viewMode === "pipeline" && (
            <div className="space-y-4">
              {/* Main Pipeline Grid */}
              <div className="grid grid-cols-4 gap-4">
                {PIPELINE_STAGES.map(stage => (
                  <SimplePipelineColumn 
                    key={stage.value}
                    stage={stage.value}
                    leads={filteredLeads}
                    onLeadClick={(lead) => {
                      handleLeadActivity(lead);
                      setSelectedLead(lead);
                    }}
                    onQuickCall={handleQuickCall}
                    onQuickEmail={handleQuickEmail}
                    onStatusChange={handleStatusChange}
                    onIcebox={(lead) => {
                      setLeadToFreeze(lead);
                      setShowIceboxModal(true);
                    }}
                    onLost={(lead) => {
                      setLeadToMarkLost(lead);
                      setShowLostReasonModal(true);
                    }}
                  />
                ))}
              </div>
              
              {/* Won / Lost / Icebox Sections */}
              <div className="grid grid-cols-3 gap-4 mt-6">
                {/* Won/Sold Section */}
                <div className="rounded-lg p-4 border-2 border-emerald-700/50 bg-emerald-900/10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-emerald-400" />
                      <h3 className="font-semibold text-emerald-400 text-sm uppercase tracking-wide">Won / Sold</h3>
                    </div>
                    <Badge variant="outline" className="border-emerald-600 text-emerald-300 font-bold">
                      {filteredLeads.filter(l => l.status === 'won').length}
                    </Badge>
                  </div>
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-2 pr-2">
                      {filteredLeads.filter(l => l.status === 'won').map(lead => (
                        <div 
                          key={lead.lead_id}
                          onClick={() => setSelectedLead(lead)}
                          className="p-2 rounded bg-emerald-900/30 border border-emerald-700/30 cursor-pointer hover:bg-emerald-900/50"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-slate-100 truncate">{lead.contact_name}</span>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="p-1 hover:bg-slate-700 rounded" onClick={(e) => e.stopPropagation()}>
                                  <MoreVertical className="h-3 w-3 text-slate-400" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
                                {ALL_LEAD_STATUSES.filter(s => s.value !== 'won').map(status => (
                                  <DropdownMenuItem 
                                    key={status.value}
                                    onClick={(e) => { 
                                      e.stopPropagation(); 
                                      if (status.value === 'lost') {
                                        setLeadToMarkLost(lead);
                                        setShowLostReasonModal(true);
                                      } else if (status.value === 'icebox') {
                                        setLeadToFreeze(lead);
                                        setShowIceboxModal(true);
                                      } else {
                                        handleStatusChange(lead.lead_id, status.value);
                                      }
                                    }}
                                    className="cursor-pointer hover:bg-slate-800"
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className={`w-2 h-2 rounded-full ${status.color}`}></div>
                                      <span className={status.textColor}>{status.label}</span>
                                    </div>
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          <p className="text-xs text-emerald-400/70 truncate">{lead.company || lead.email}</p>
                        </div>
                      ))}
                      {filteredLeads.filter(l => l.status === 'won').length === 0 && (
                        <p className="text-center text-emerald-500/50 text-sm py-4">No won leads</p>
                      )}
                    </div>
                  </ScrollArea>
                </div>

                {/* Lost/Archive Section */}
                <div className="rounded-lg p-4 border-2 border-red-700/50 bg-red-900/10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Archive className="h-5 w-5 text-red-400" />
                      <h3 className="font-semibold text-red-400 text-sm uppercase tracking-wide">Lost / Archive</h3>
                    </div>
                    <Badge variant="outline" className="border-red-600 text-red-300 font-bold">
                      {filteredLeads.filter(l => l.status === 'lost').length}
                    </Badge>
                  </div>
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-2 pr-2">
                      {filteredLeads.filter(l => l.status === 'lost').map(lead => (
                        <div 
                          key={lead.lead_id}
                          onClick={() => setSelectedLead(lead)}
                          className="p-2 rounded bg-red-900/30 border border-red-700/30 cursor-pointer hover:bg-red-900/50"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-slate-100 truncate">{lead.contact_name}</span>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="p-1 hover:bg-slate-700 rounded" onClick={(e) => e.stopPropagation()}>
                                  <MoreVertical className="h-3 w-3 text-slate-400" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
                                {ALL_LEAD_STATUSES.filter(s => s.value !== 'lost').map(status => (
                                  <DropdownMenuItem 
                                    key={status.value}
                                    onClick={(e) => { 
                                      e.stopPropagation(); 
                                      if (status.value === 'icebox') {
                                        setLeadToFreeze(lead);
                                        setShowIceboxModal(true);
                                      } else {
                                        handleStatusChange(lead.lead_id, status.value);
                                      }
                                    }}
                                    className="cursor-pointer hover:bg-slate-800"
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className={`w-2 h-2 rounded-full ${status.color}`}></div>
                                      <span className={status.textColor}>{status.label}</span>
                                    </div>
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          <p className="text-xs text-red-400/70 truncate">{lead.lost_reason || lead.company || lead.email}</p>
                        </div>
                      ))}
                      {filteredLeads.filter(l => l.status === 'lost').length === 0 && (
                        <p className="text-center text-red-500/50 text-sm py-4">No lost leads</p>
                      )}
                    </div>
                  </ScrollArea>
                </div>

                {/* Icebox/Nurture Section */}
                <div className="rounded-lg p-4 border-2 border-cyan-700/50 bg-cyan-900/10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Snowflake className="h-5 w-5 text-cyan-400" />
                      <h3 className="font-semibold text-cyan-400 text-sm uppercase tracking-wide">Icebox / Nurture</h3>
                    </div>
                    <Badge variant="outline" className="border-cyan-600 text-cyan-300 font-bold">
                      {frozenLeads.length}
                    </Badge>
                  </div>
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-2 pr-2">
                      {frozenLeads.map(lead => (
                        <div 
                          key={lead.lead_id}
                          onClick={() => setSelectedLead(lead)}
                          className="p-2 rounded bg-cyan-900/30 border border-cyan-700/30 cursor-pointer hover:bg-cyan-900/50"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-slate-100 truncate">{lead.contact_name}</span>
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-cyan-400 hover:bg-cyan-900/50"
                                onClick={(e) => { e.stopPropagation(); handleUnfreezeLead(lead.lead_id); }}
                                title="Unfreeze lead"
                              >
                                <Flame className="h-3 w-3" />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="p-1 hover:bg-slate-700 rounded" onClick={(e) => e.stopPropagation()}>
                                    <MoreVertical className="h-3 w-3 text-slate-400" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
                                  {ALL_LEAD_STATUSES.filter(s => s.value !== 'icebox').map(status => (
                                    <DropdownMenuItem 
                                      key={status.value}
                                      onClick={(e) => { 
                                        e.stopPropagation(); 
                                        if (status.value === 'lost') {
                                          setLeadToMarkLost(lead);
                                          setShowLostReasonModal(true);
                                        } else {
                                          handleUnfreezeLead(lead.lead_id);
                                          handleStatusChange(lead.lead_id, status.value);
                                        }
                                      }}
                                      className="cursor-pointer hover:bg-slate-800"
                                    >
                                      <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${status.color}`}></div>
                                        <span className={status.textColor}>{status.label}</span>
                                      </div>
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                          <p className="text-xs text-cyan-400/70 truncate">
                            {lead.freeze_reason || lead.company || lead.email}
                          </p>
                          {lead.wake_up_date && (
                            <p className="text-xs text-cyan-500 mt-1">
                              Wake: {new Date(lead.wake_up_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      ))}
                      {frozenLeads.length === 0 && (
                        <p className="text-center text-cyan-500/50 text-sm py-4">No frozen leads</p>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>
              
              {/* Demo Simulation Buttons - gated to non-production or explicit demo mode */}
              {DEMO_ENABLED && (
                <div className="flex items-center justify-between pt-4 border-t border-slate-800">
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={simulateEmailLead}
                      className="border-purple-600 text-purple-400 hover:bg-purple-900/30"
                    >
                      <Bot className="h-3 w-3 mr-1" />
                      Simulate Email Lead (HomeAdvisor)
                    </Button>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={seedTestData}
                    className="border-slate-600 text-slate-400 hover:text-slate-200"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Seed Test Leads
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* List View */}
          {viewMode === "list" && (
            <div className="bg-slate-800 rounded-lg border border-slate-700">
              <table className="w-full">
                <thead className="bg-slate-900 border-b border-slate-700">
                  <tr>
                    <th className="text-left p-3 font-medium text-slate-300">Contact</th>
                    <th className="text-left p-3 font-medium text-slate-300">Service</th>
                    <th className="text-left p-3 font-medium text-slate-300">Status</th>
                    <th className="text-left p-3 font-medium text-slate-300">Priority</th>
                    <th className="text-left p-3 font-medium text-slate-300">Next Action</th>
                    <th className="text-left p-3 font-medium text-slate-300">Assigned</th>
                    <th className="text-left p-3 font-medium text-slate-300"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map(lead => (
                    <tr 
                      key={lead.lead_id} 
                      className="border-b border-slate-700 hover:bg-slate-700/50 cursor-pointer"
                      onClick={() => setSelectedLead(lead)}
                    >
                      <td className="p-3">
                        <div>
                          <p className="font-medium text-slate-100">{lead.contact_name}</p>
                          <p className="text-sm text-slate-400">{lead.phone}</p>
                        </div>
                      </td>
                      <td className="p-3 text-sm text-slate-300">{SERVICE_TYPES.find(s => s.value === lead.service_type)?.label}</td>
                      <td className="p-3"><StatusBadge status={lead.status} /></td>
                      <td className="p-3"><PriorityBadge priority={lead.priority} /></td>
                      <td className="p-3 text-sm text-slate-300">{NEXT_ACTIONS.find(a => a.value === lead.next_action)?.label}</td>
                      <td className="p-3 text-sm text-slate-300">{lead.assigned_name || "-"}</td>
                      <td className="p-3">
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredLeads.length === 0 && (
                <p className="text-center text-slate-500 py-8">No leads found</p>
              )}
            </div>
          )}
        </TabsContent>

        {/* Clients Tab */}
        <TabsContent value="clients" className="mt-4">
          <div className="bg-slate-800 rounded-lg border border-slate-700">
            <table className="w-full">
              <thead className="bg-slate-900 border-b border-slate-700">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-300">Name</th>
                  <th className="text-left p-3 font-medium text-slate-300">Contact</th>
                  <th className="text-left p-3 font-medium text-slate-300">Address</th>
                  <th className="text-left p-3 font-medium text-slate-300">Source</th>
                  <th className="text-left p-3 font-medium text-slate-300 w-16">Action</th>
                </tr>
              </thead>
              <tbody>
                {clients.map(client => (
                  <tr 
                    key={client.client_id} 
                    className="border-b border-slate-700 hover:bg-slate-700/50 cursor-pointer transition-colors group"
                    onClick={() => setSelectedClient(client)}
                  >
                    <td className="p-3">
                      <p className="font-medium text-slate-100 group-hover:text-emerald-400 transition-colors">{client.name}</p>
                    </td>
                    <td className="p-3">
                      <p className="text-sm text-slate-200">{client.phone}</p>
                      <p className="text-sm text-slate-400">{client.email}</p>
                    </td>
                    <td className="p-3 text-sm text-slate-300">{client.address}</td>
                    <td className="p-3">
                      {client.lead_id && <Badge variant="outline" className="border-slate-600 text-slate-300">From Lead</Badge>}
                    </td>
                    <td className="p-3">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                        onClick={(e) => { e.stopPropagation(); setSelectedClient(client); }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {clients.length === 0 && (
              <p className="text-center text-slate-500 py-8">No clients yet. Convert leads to create clients.</p>
            )}
          </div>
          
          {/* Client Detail Modal */}
          <Dialog open={!!selectedClient} onOpenChange={() => setSelectedClient(null)}>
            <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 text-slate-100">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 text-xl">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center">
                    <Users className="h-5 w-5 text-emerald-400" />
                  </div>
                  CLIENT DOSSIER
                </DialogTitle>
              </DialogHeader>
              
              {selectedClient && (
                <div className="space-y-6">
                  {/* Client Info Header */}
                  <div className="p-4 bg-slate-800 rounded-lg border border-slate-700">
                    <h3 className="text-xl font-bold text-slate-100 mb-2">{selectedClient.name}</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-slate-300">
                        <Phone className="h-4 w-4 text-emerald-400" />
                        <a href={`tel:${selectedClient.phone}`} className="hover:text-emerald-400">{selectedClient.phone || "N/A"}</a>
                      </div>
                      <div className="flex items-center gap-2 text-slate-300">
                        <Mail className="h-4 w-4 text-blue-400" />
                        <a href={`mailto:${selectedClient.email}`} className="hover:text-blue-400">{selectedClient.email || "N/A"}</a>
                      </div>
                      <div className="flex items-center gap-2 text-slate-300 col-span-2">
                        <MapPin className="h-4 w-4 text-amber-400" />
                        <span>{selectedClient.address || "No address"}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Tabs for Projects, Invoices, Contact Info */}
                  <Tabs defaultValue="projects" className="w-full">
                    <TabsList className="bg-slate-800 border border-slate-700">
                      <TabsTrigger value="projects" className="data-[state=active]:bg-emerald-600">Projects</TabsTrigger>
                      <TabsTrigger value="invoices" className="data-[state=active]:bg-emerald-600">Invoices</TabsTrigger>
                      <TabsTrigger value="notes" className="data-[state=active]:bg-emerald-600">Notes</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="projects" className="mt-4">
                      <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50 text-center text-slate-500">
                        <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No projects linked to this client yet.</p>
                        <Button size="sm" className="mt-3 bg-emerald-600 hover:bg-emerald-500">
                          <Plus className="h-4 w-4 mr-1" />
                          Create Project
                        </Button>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="invoices" className="mt-4">
                      <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50 text-center text-slate-500">
                        <DollarSign className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No invoices for this client.</p>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="notes" className="mt-4">
                      <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                        <Textarea 
                          placeholder="Add notes about this client..."
                          className="bg-slate-900 border-slate-700 text-slate-100 min-h-[100px]"
                          defaultValue={selectedClient.notes || ""}
                        />
                        <Button size="sm" className="mt-2 bg-emerald-600 hover:bg-emerald-500">
                          Save Notes
                        </Button>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="mt-4">
          {dashboardStats && (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="pt-4">
                    <p className="text-sm text-slate-400">New This Week</p>
                    <p className="text-3xl font-bold text-slate-100">{dashboardStats.new_this_week}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="pt-4">
                    <p className="text-sm text-slate-400">Contact Rate (24h)</p>
                    <p className="text-3xl font-bold text-slate-100">{dashboardStats.contacted_rate_24h}%</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="pt-4">
                    <p className="text-sm text-slate-400">Won This Week</p>
                    <p className="text-3xl font-bold text-emerald-400">{dashboardStats.won_this_week}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="pt-4">
                    <p className="text-sm text-slate-400">Lost This Week</p>
                    <p className="text-3xl font-bold text-red-400">{dashboardStats.lost_this_week}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Alerts */}
              <div className="grid grid-cols-2 gap-4">
                {/* Follow-ups Due */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2 text-slate-200">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      Follow-Ups Needed
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboardStats.follow_ups_due_today?.length > 0 || dashboardStats.follow_ups_overdue?.length > 0 ? (
                      <div className="space-y-2">
                        {dashboardStats.follow_ups_overdue?.slice(0, 3).map((l, i) => (
                          <div key={i} className="p-2 bg-red-900/30 border border-red-800/50 rounded text-sm">
                            <span className="font-medium text-slate-200">{l.contact_name}</span>
                            <span className="text-red-400 ml-2">Overdue: {l.follow_up_date}</span>
                          </div>
                        ))}
                        {dashboardStats.follow_ups_due_today?.slice(0, 3).map((l, i) => (
                          <div key={i} className="p-2 bg-amber-900/30 border border-amber-800/50 rounded text-sm">
                            <span className="font-medium text-slate-200">{l.contact_name}</span>
                            <span className="text-amber-400 ml-2">Due today</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">No follow-ups due</p>
                    )}
                  </CardContent>
                </Card>

                {/* Not Contacted */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2 text-slate-200">
                      <Clock className="h-4 w-4 text-red-500" />
                      Not Contacted (24h+)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboardStats.not_contacted_24h?.length > 0 ? (
                      <div className="space-y-2">
                        {dashboardStats.not_contacted_24h.slice(0, 5).map((l, i) => (
                          <div key={i} className="p-2 bg-red-900/30 border border-red-800/50 rounded text-sm flex items-center justify-between">
                            <span className="font-medium text-slate-200">{l.contact_name}</span>
                            <Button size="sm" variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-700" onClick={() => {
                              const lead = leads.find(lead => lead.lead_id === l.lead_id);
                              if (lead) setSelectedLead(lead);
                            }}>
                              Contact Now
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        All new leads contacted!
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Pipeline Breakdown */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-sm text-slate-200">Pipeline Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-3">
                    {PIPELINE_STAGES.map(stage => {
                      // Count leads in this stage (including legacy statuses)
                      const stageCount = leads.filter(l => getNormalizedStatus(l.status) === stage.value).length;
                      return (
                        <div key={stage.value} className="text-center p-4 bg-slate-900 border border-slate-700 rounded-lg">
                          <div className={`w-4 h-4 rounded-full ${stage.color} mx-auto mb-2`} />
                          <p className="text-3xl font-bold text-slate-100">{stageCount}</p>
                          <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">{stage.label}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Source Analysis */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-sm text-slate-200">Lead Sources Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-3">
                    {Object.entries(dashboardStats.by_source || {}).map(([source, data]) => (
                      <div key={source} className="p-3 bg-slate-900 border border-slate-700 rounded">
                        <p className="font-medium text-sm text-slate-200">{LEAD_SOURCES.find(s => s.value === source)?.label || source}</p>
                        <p className="text-lg font-bold text-slate-100">{data.total}</p>
                        <p className="text-xs text-emerald-400">{data.won} won ({data.total > 0 ? Math.round(data.won/data.total*100) : 0}%)</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <LeadDetailModal 
        lead={selectedLead}
        isOpen={!!selectedLead}
        onClose={() => setSelectedLead(null)}
        onUpdate={() => {
          loadData();
          // Refresh the selected lead
          if (selectedLead) {
            API.get(`/leads/${selectedLead.lead_id}`).then(res => setSelectedLead(res.data));
          }
        }}
        teamMembers={teamMembers}
      />

      <CreateLeadModal 
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={loadData}
        teamMembers={teamMembers}
        onDuplicate={({ payload, duplicateLeadId }) => {
          setDuplicateDialog({ open: true, payload, duplicateLeadId });
        }}
      />
      
      {/* Duplicate Lead Dialog */}
      <Dialog open={duplicateDialog.open} onOpenChange={(open) => setDuplicateDialog(prev => ({ ...prev, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Possible duplicate lead</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 mb-4">
            A lead already exists with matching contact info (email and/or phone). What do you want to do?
          </p>
          <DialogFooter className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                // Cancel: close only this dialog, keep create modal and form data intact
                setDuplicateDialog(prev => ({ ...prev, open: false }));
              }}
            >
              Cancel
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={async () => {
                  const { duplicateLeadId } = duplicateDialog;
                  if (!duplicateLeadId) return;
                  setShowCreateModal(false);
                  setDuplicateDialog({ open: false, payload: null, duplicateLeadId: null });
                  setActiveTab("leads");
                  setSelectedLead(null);

                  const existing = leads.find((l) => l.lead_id === duplicateLeadId);
                  if (existing) {
                    setSelectedLead(existing);
                  } else {
                    try {
                      const res = await API.get(`/leads/${duplicateLeadId}`);
                      setSelectedLead(res.data);
                    } catch (err) {
                      console.error(err);
                      toast.error("Failed to open existing duplicate lead");
                    }
                  }
                }}
              >
                Open Existing
              </Button>
              {user?.role && (user?.role === "owner" || user?.role === "office_manager") && (
                <Button
                  type="button"
                  onClick={async () => {
                    const { payload } = duplicateDialog;
                    if (!payload) return;
                    try {
                      const res = await API.post("/leads?allow_duplicate=true", payload);
                      const created = res.data || res;
                      toast.success("Lead created (marked as possible duplicate)");
                      setDuplicateDialog({ open: false, payload: null, duplicateLeadId: null });
                      setShowCreateModal(false);
                      await loadData();
                      if (created && created.lead_id) {
                        setActiveTab("leads");
                        setSelectedLead(created);
                      }
                    } catch (err) {
                      const status = err?.response?.status;
                      const data = err?.response?.data;
                      if (status === 403 && data?.detail) {
                        toast.error(data.detail);
                      } else {
                        toast.error("Failed to create duplicate lead");
                      }
                    }
                  }}
                >
                  Create Anyway
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* THE ICEBOX MODAL - Wake Up Date Picker */}
      <Dialog open={showIceboxModal} onOpenChange={setShowIceboxModal}>
        <DialogContent className="bg-slate-900 border-cyan-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-cyan-300">
              <Snowflake className="h-5 w-5" />
              Freeze Lead - Set Wake Up Date
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {leadToFreeze && (
              <div className="mb-4 p-3 bg-slate-800 rounded-lg border border-slate-700">
                <p className="text-slate-200 font-medium">{leadToFreeze.contact_name}</p>
                <p className="text-slate-400 text-sm">{leadToFreeze.phone}</p>
              </div>
            )}
            <Label className="text-slate-300">When should this lead wake up?</Label>
            <Input 
              type="date"
              value={wakeUpDate}
              onChange={(e) => setWakeUpDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="mt-2 bg-slate-800 border-slate-700 text-slate-200"
            />
            <p className="text-xs text-cyan-500 mt-2">
              The lead will automatically reappear in &ldquo;New Lead&rdquo; column on this date with a &ldquo;WAKE UP&rdquo; tag.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIceboxModal(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              onClick={handleFreezeLead}
              className="bg-cyan-600 hover:bg-cyan-500 text-white"
            >
              <Snowflake className="h-4 w-4 mr-2" />
              Freeze Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* THE WHY BUTTON MODAL - Lost Reason */}
      <Dialog open={showLostReasonModal} onOpenChange={setShowLostReasonModal}>
        <DialogContent className="bg-slate-900 border-red-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertOctagon className="h-5 w-5" />
              Reason for Loss?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {leadToMarkLost && (
              <div className="p-3 bg-slate-800 rounded-lg border border-slate-700">
                <p className="text-slate-200 font-medium">{leadToMarkLost.contact_name}</p>
                <p className="text-slate-400 text-sm">{leadToMarkLost.phone}</p>
              </div>
            )}
            
            <div>
              <Label className="text-slate-300">Why did we lose this lead?</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {LOST_REASONS.map(reason => (
                  <Button
                    key={reason.value}
                    variant={selectedLostReason === reason.value ? "default" : "outline"}
                    className={`justify-start ${
                      selectedLostReason === reason.value 
                        ? "bg-red-600 hover:bg-red-500 text-white" 
                        : "border-slate-600 text-slate-300 hover:bg-slate-800"
                    }`}
                    onClick={() => setSelectedLostReason(reason.value)}
                  >
                    {reason.label}
                  </Button>
                ))}
              </div>
            </div>
            
            <div>
              <Label className="text-slate-300">Additional Notes (Optional)</Label>
              <Textarea 
                value={lostNotes}
                onChange={(e) => setLostNotes(e.target.value)}
                placeholder="Any details about why this lead was lost..."
                className="mt-2 bg-slate-800 border-slate-700 text-slate-200"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLostReasonModal(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              onClick={handleMarkLost}
              disabled={!selectedLostReason}
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Mark as Lost
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* WON / CONVERT MODAL */}
      <Dialog open={showWonModal} onOpenChange={setShowWonModal}>
        <DialogContent className="bg-slate-900 border-emerald-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-400">
              <Trophy className="h-5 w-5" />
              🎉 Congratulations! Lead Won!
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {leadToWin && (
              <div className="p-4 bg-emerald-900/30 border border-emerald-700/50 rounded-lg mb-4">
                <p className="text-emerald-200 font-medium text-lg">{leadToWin.contact_name}</p>
                <p className="text-emerald-400 text-sm">{leadToWin.phone}</p>
                <p className="text-emerald-400/80 text-sm mt-1">
                  {SERVICE_TYPES.find(s => s.value === leadToWin.service_type)?.label || leadToWin.service_type}
                </p>
              </div>
            )}
            <p className="text-slate-300 mb-4">
              Would you like to convert this lead into an active client and project?
            </p>
            <div className="p-3 bg-slate-800 border border-slate-700 rounded-lg">
              <p className="text-sm text-slate-400">This will:</p>
              <ul className="text-sm text-slate-300 mt-2 space-y-1">
                <li>• Create a new Client record</li>
                <li>• Create a linked Project</li>
                <li>• Mark this lead as &ldquo;Won&rdquo;</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowWonModal(false); setLeadToWin(null); }} className="border-slate-600">
              Just Mark as Won
            </Button>
            <Button 
              onClick={handleWonConvert}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              <Trophy className="h-4 w-4 mr-2" />
              Convert to Active Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadsClientsPage;
